import React from 'react';
import { format, parseISO } from 'date-fns';
import { ChartContainer, ChartTooltip } from '@/shared/ui/chart';
import { XAxis, YAxis, CartesianGrid, Bar, BarChart, LabelList } from 'recharts';
import { DataUsage } from '@/shared/types/serviceLineTypes';
import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { formatDataUsage } from '@/shared/utils/format-utils';
import EmptyView from './EmptyView';
import { BAR_CHART_CATEGORY_GAP, BAR_CHART_MAX_BAR_SIZE } from '@/domains/global-dashboard/utils/chart-utils';

interface DataUsageBarChartProps {
    dataUsages: DataUsage[];
}

export function DataUsageBarChart({ dataUsages }: DataUsageBarChartProps) {
    // Transform data for the chart
    const chartData = React.useMemo(() => {
        return dataUsages.length > 0 ? dataUsages
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
            .map((usage, index, array) => {
                const previousUsage = index > 0 ? array[index - 1].totalDataUsageGB : null;
                const trend = previousUsage !== null ?
                    (usage.totalDataUsageGB > previousUsage ? 'up' :
                        usage.totalDataUsageGB < previousUsage ? 'down' : 'stable') : 'stable';

                return {
                    month: format(parseISO(usage.startDate), 'MMM yyyy'),
                    shortMonth: format(parseISO(usage.startDate), 'MMM'),
                    usage: usage.totalDataUsageGB ?? 0,
                    date: usage.startDate,
                    trend,
                    previousUsage
                };
            }) : [];
    }, [dataUsages]);

    return (
        <div className="h-96">
            {chartData.length > 0 ? (
                <ChartContainer
                    config={{
                        usage: {
                            label: "Data Usage (GB)",
                            color: "hsl(var(--chart-1))",
                        },
                    }}
                    className="w-full h-full"
                >
                    <BarChart
                        data={chartData}
                        margin={{ top: 40, right: 0, left: 0, bottom: 0 }}
                        maxBarSize={BAR_CHART_MAX_BAR_SIZE}
                        barCategoryGap={BAR_CHART_CATEGORY_GAP}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" opacity={0.3} />
                        <XAxis
                            dataKey="month"
                            tick={{ fontSize: 12, fontWeight: 'bold' }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fontWeight: 'bold' }}
                            tickFormatter={(value) => formatDataUsage(Number(value))}
                            domain={[0, 'auto']}
                        />

                        <ChartTooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    return (
                                        <div className="rounded-lg border bg-background p-3 shadow-md">
                                            <div className="grid gap-2">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium">{data.month}</span>
                                                    <span className="text-xs">
                                                        {formatDataUsage(Number(data.usage))}
                                                    </span>
                                                    {data.trend !== 'stable' && (
                                                        <div className="flex items-center gap-1 text-xs mt-1">
                                                            {data.trend === 'up' ? (
                                                                <TrendingUp className="h-3 w-3 text-red-500" />
                                                            ) : (
                                                                <TrendingDown className="h-3 w-3 text-green-500" />
                                                            )}
                                                            <span className={data.trend === 'up' ? 'text-red-500' : 'text-green-500'}>
                                                                {data.trend === 'up' ? 'Increased' : 'Decreased'} from previous month
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Bar
                            dataKey="usage"
                            fill="hsl(var(--primary))"
                            radius={[4, 4, 0, 0]}
                        >
                            <LabelList
                                dataKey="usage"
                                position="top"
                                formatter={(value: number) => value > 0 ? formatDataUsage(Number(value)) : ''}
                                style={{ fontSize: 12, fontWeight: 600, fill: 'hsl(var(--foreground))' }}
                            />
                        </Bar>
                    </BarChart>
                </ChartContainer>
            ) : (
                <EmptyView
                    icon={BarChart3}
                    title="No Usage Data Available"
                    description="This service line doesn't have any usage data to display."
                />
            )}
        </div>
    );
}
